import {useState, useEffect} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@sakura-grp/ui/drawer";
import {Button} from "@sakura-grp/ui/button";
import {toast} from "@sakura-grp/ui/sonner";
import {updateCustomerRestrictions} from "@/lib/api/customers";
import {useDirtyDialogGuard} from "@/hooks/use-dirty-dialog-guard";

interface NgEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
  /** true = NG (電話禁止) */
  phoneNg: boolean;
  /** true = NG (メッセージ禁止) */
  messageNg: boolean;
  /** true = NG (DM禁止) */
  dmNg: boolean;
  /** true = NG (訪問禁止) */
  visitNg: boolean;
}

export function NgEditSheet({
  open,
  onOpenChange,
  customerId,
  phoneNg,
  messageNg,
  dmNg,
  visitNg,
}: NgEditSheetProps) {
  const [localPhoneNg, setLocalPhoneNg] = useState(phoneNg);
  const [localMessageNg, setLocalMessageNg] = useState(messageNg);
  const [localDmNg, setLocalDmNg] = useState(dmNg);
  const [localVisitNg, setLocalVisitNg] = useState(visitNg);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setLocalPhoneNg(phoneNg);
      setLocalMessageNg(messageNg);
      setLocalDmNg(dmNg);
      setLocalVisitNg(visitNg);
    }
  }, [open, phoneNg, messageNg, dmNg, visitNg]);

  const mutation = useMutation({
    mutationFn: () =>
      updateCustomerRestrictions(customerId, {
        allowPhone: !localPhoneNg,
        allowMessage: !localMessageNg,
        allowDm: !localDmNg,
        allowVisit: !localVisitNg,
      }),
    onSuccess: () => {
      toast.success("NG設定を更新しました");
      queryClient.invalidateQueries({queryKey: ["customer-detail", customerId]});
      queryClient.invalidateQueries({queryKey: ["tab-customers"]});
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const hasChanges =
    localPhoneNg !== phoneNg ||
    localMessageNg !== messageNg ||
    localDmNg !== dmNg ||
    localVisitNg !== visitNg;

  const {DirtyConfirmDialog, requestClose} = useDirtyDialogGuard({
    isDirty: hasChanges,
    onClose: () => onOpenChange(false),
  });

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange} dismissible={!hasChanges}>
        <DrawerContent className="px-lg pb-safe-min">
          <DrawerHeader className="pb-md">
            <DrawerTitle>NG設定の編集</DrawerTitle>
            <DrawerDescription>この顧客への連絡制限を設定します</DrawerDescription>
          </DrawerHeader>

          <div className="space-y-4 py-2">
            <label className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-sm font-medium">電話NG</span>
              <button
                type="button"
                role="switch"
                aria-checked={localPhoneNg}
                onClick={() => setLocalPhoneNg(!localPhoneNg)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors ${
                  localPhoneNg ? "bg-red-500" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block size-5 translate-y-0.5 rounded-full bg-white shadow-sm transition-transform ${
                    localPhoneNg ? "translate-x-5.5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </label>

            <label className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-sm font-medium">メッセージNG</span>
              <button
                type="button"
                role="switch"
                aria-checked={localMessageNg}
                onClick={() => setLocalMessageNg(!localMessageNg)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors ${
                  localMessageNg ? "bg-red-500" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block size-5 translate-y-0.5 rounded-full bg-white shadow-sm transition-transform ${
                    localMessageNg ? "translate-x-5.5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </label>

            <label className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-sm font-medium">DM NG</span>
              <button
                type="button"
                role="switch"
                aria-checked={localDmNg}
                onClick={() => setLocalDmNg(!localDmNg)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors ${
                  localDmNg ? "bg-red-500" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block size-5 translate-y-0.5 rounded-full bg-white shadow-sm transition-transform ${
                    localDmNg ? "translate-x-5.5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </label>

            <label className="flex items-center justify-between rounded-lg border p-3">
              <span className="text-sm font-medium">訪問NG</span>
              <button
                type="button"
                role="switch"
                aria-checked={localVisitNg}
                onClick={() => setLocalVisitNg(!localVisitNg)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors ${
                  localVisitNg ? "bg-red-500" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block size-5 translate-y-0.5 rounded-full bg-white shadow-sm transition-transform ${
                    localVisitNg ? "translate-x-5.5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </label>
          </div>

          <DrawerFooter className="p-0 pt-md flex flex-col gap-md">
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending || !hasChanges}
            >
              {mutation.isPending ? "保存中..." : "保存する"}
            </Button>
            <Button
              variant="outline"
              onClick={requestClose}
              disabled={mutation.isPending}
            >
              やめる
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      <DirtyConfirmDialog />
    </>
  );
}
